import React from "react";
import { createContext, ReactNode, use, useMemo, useState } from "react";
import { useHotkeysEnabled } from "src/hooks/settings";

interface HotkeyToggles {
  areHotkeysEnabled: boolean;
  setAreHotkeysEnabled: (areHotkeysEnabled: boolean) => void;
}

const HotkeyToggleContext = createContext<HotkeyToggles>({
  areHotkeysEnabled: true,
  setAreHotkeysEnabled: () => null,
});

export const HotkeyToggleProvider = ({ children }: { children: ReactNode }) => {
  const [areHotkeysEnabled, setAreHotkeysEnabled] = useState(true);

  const hotkeyToggles = useMemo(() => {
    return {
      areHotkeysEnabled,
      setAreHotkeysEnabled,
    };
  }, [areHotkeysEnabled, setAreHotkeysEnabled]);

  return (
    <HotkeyToggleContext value={hotkeyToggles}>{children}</HotkeyToggleContext>
  );
};

export const useSetAreHotkeysEnabled = () => {
  const { setAreHotkeysEnabled } = use(HotkeyToggleContext);
  return setAreHotkeysEnabled;
};

export const DisableHotkeysInChildren = ({
  children,
}: {
  children: ReactNode;
}) => {
  const setAreHotkeysEnabled = useSetAreHotkeysEnabled();

  React.useEffect(() => {
    setAreHotkeysEnabled(false);
    return () => {
      setAreHotkeysEnabled(true);
    };
  }, [setAreHotkeysEnabled]);

  return children;
};

export const useAreHotkeysEnabled = () => {
  const { areHotkeysEnabled } = use(HotkeyToggleContext);
  const hotkeysEnabledFromSettings = useHotkeysEnabled();
  return areHotkeysEnabled && hotkeysEnabledFromSettings;
};

/**
 * Suspends only while a grid cell is selected or being edited, independent
 * of the user's opt-in "Hotkeys" setting.
 */
export const useAreGridSafeHotkeysEnabled = () => {
  const { areHotkeysEnabled } = use(HotkeyToggleContext);
  return areHotkeysEnabled;
};
